// Claim Number is deliberately not in this list — it's validated explicitly in
// validateClaimForm because it applies to drafts too, not only on submit.
export const REQUIRED_CLAIM_FIELDS = [
  { key: "claimType", label: "Claim Type" },
  { key: "dateIssueOccurred", label: "Date Issue Occurred" },
  { key: "description", label: "Claim Description" },
  { key: "totalClaimAmount", label: "Total Claim Amount" },
];

export const getTodayLocalDate = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

export const parseCurrencyValue = (value) => {
  if (value == null || value === "") return null;
  const cleaned = String(value).replace(/[^0-9.-]/g, "");
  if (!cleaned || cleaned === "-" || cleaned === ".") return null;
  const n = Number(cleaned);
  return Number.isNaN(n) ? null : n;
};

export const formatCurrencyInputValue = (value) => {
  const n = parseCurrencyValue(value);
  if (n == null) return "";
  return n.toFixed(2);
};

export const sanitizeCurrencyInput = (raw) => {
  const cleaned = String(raw).replace(/[^0-9.]/g, "");
  const parts = cleaned.split(".");
  if (parts.length <= 1) return cleaned;
  return `${parts[0]}.${parts.slice(1).join("").slice(0, 2)}`;
};

export const CLAIM_NUMBER_MAX_LENGTH = 50;

/**
 * @param {Object} form
 * @param {Object} [options]
 * @param {boolean} [options.submit] - full validation; drafts only check what's needed to save
 * @param {boolean} [options.isNewClaim] - only a new claim's number is user-entered and validated
 */
export const validateClaimForm = (form, { submit = false, isNewClaim = true } = {}) => {
  const errors = {};

  // Claim Number is user-entered (LGX-1047) and required on every save, not just on submit —
  // a claim can't be stored without one. An existing claim already has its number and can't
  // change it, so there's nothing to validate on that path.
  if (isNewClaim) {
    const claimNumber = String(form.claimNumber ?? "").trim();
    if (!claimNumber) {
      errors.claimNumber = "Claim Number is required.";
    } else if (claimNumber.length > CLAIM_NUMBER_MAX_LENGTH) {
      errors.claimNumber = `Claim Number must be at most ${CLAIM_NUMBER_MAX_LENGTH} characters.`;
    }
  }

  if (!submit) return { valid: Object.keys(errors).length === 0, errors };

  REQUIRED_CLAIM_FIELDS.forEach(({ key, label }) => {
    if (key === "totalClaimAmount") {
      const amount = parseCurrencyValue(form.totalClaimAmount);
      if (amount == null || amount <= 0) {
        errors.totalClaimAmount = `${label} is required and must be greater than zero.`;
      }
      return;
    }

    const value = form[key];
    if (!value || (typeof value === "string" && !value.trim())) {
      errors[key] = `${label} is required.`;
    }
  });

  if (form.mode === "standalone" && !form.shipmentNumber?.trim()) {
    errors.shipmentNumber = "Shipment Number is required.";
  }

  if (form.relatedStopId == null || form.relatedStopId === "") {
    errors.relatedStopId = "Related Stop is required.";
  }

  return { valid: Object.keys(errors).length === 0, errors };
};
